import { useEntityStore } from '@/lib/sync/entity-store';
import type { RecordingMeta } from '@/lib/recordings/types/types';

const anyCapturing = (recordings: ReadonlyMap<string, RecordingMeta>): boolean => {
  for (const rec of recordings.values()) {
    if (rec.status === 'recording') return true;
  }
  return false;
};

/**
 * Whether any recording is actively capturing audio right now.
 *
 * Narrower than `useActiveRecording`, which also reports post-capture stages
 * (uploading, transcribing). Only live capture is unsafe to interrupt, and
 * this lives in `lib/` so non-recording features can ask without reaching
 * across a feature boundary.
 */
export function useIsCapturing(): boolean {
  return useEntityStore((s) => anyCapturing(s.recordings));
}

/**
 * The same question, asked outside React.
 *
 * For callers that read on their own schedule rather than re-rendering — an
 * animation loop, say. Returns false when no workspace is active yet instead
 * of throwing, so a caller mounted above the sync provider cannot crash the
 * app before sign-in.
 *
 * Prefer `useIsCapturing` in components; this exists for the cases a hook
 * cannot serve.
 */
export function isCapturingNow(): boolean {
  try {
    return anyCapturing(useEntityStore.getState().recordings);
  } catch {
    return false;
  }
}
